import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProjectDetailsLayoutComponent } from '@app/components/project-details-layout/project-details-layout.component';
import type { UserAccount } from '@app/models/account.model';
import { ProjectAdminResponse } from '@app/models/admin.model';
import { UserService } from '@app/services/admin/user.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-details-tab',
  standalone: true,
  imports: [CommonModule, ProjectDetailsLayoutComponent],
  templateUrl: './details-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailsTabComponent {
  project = input.required<ProjectAdminResponse | null>();

  files = input.required<any[]>();
  contactUserId = input<string | null>(null);

  private readonly userService = inject(UserService);
  private readonly destroyRef = inject(DestroyRef);

  readonly contactUser = signal<UserAccount | null>(null);

  readonly loadingProject = signal(false);
  readonly loadingContact = signal(false);
  readonly isLoading = computed(() => !this.project() || this.loadingContact());
  readonly error = signal<string | null>(null);

  constructor() {
    effect(() => {
      const contactId = this.contactUserId();
      this.contactUser.set(null);

      if (!contactId) {
        this.loadingContact.set(false);
        return;
      }

      this.loadingContact.set(true);
      this.userService
        .getUserById(contactId)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => this.loadingContact.set(false))
        )
        .subscribe({
          next: (user: UserAccount) => this.contactUser.set(user),
          error: () => this.contactUser.set(null),
        });
    });
  }
}
